import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, FileText, Mail, Phone, Download, Users } from "lucide-react";

const STAGES = ["NEW", "REVIEWED", "SHORTLISTED", "INTERVIEW", "OFFER", "JOINED", "CLOSED"];
const STAGE_STYLE = {
  NEW: "bg-blue-50 text-blue-700", REVIEWED: "bg-slate-100 text-slate-600",
  SHORTLISTED: "bg-violet-50 text-violet-700", INTERVIEW: "bg-amber-50 text-amber-700",
  OFFER: "bg-[#C79A3B]/12 text-[#8a6a1f]", JOINED: "bg-emerald-50 text-emerald-700", CLOSED: "bg-slate-100 text-slate-400",
};

export default function RecruitmentApplicants() {
  const nav = useNavigate();
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get(`/recruitment/${id}/applications`, { params })
      .then((r) => setApps(r.data || []))
      .catch((e) => { toast.error(apiError(e)); setApps([]); });
  };
  useEffect(() => { api.get(`/recruitment/${id}`).then((r) => setOpp(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, filter]);

  const changeStage = async (aid, pipeline_status) => {
    try { await api.patch(`/recruitment/${id}/applications/${aid}`, { pipeline_status }); toast.success("Stage updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadCv = async (aid) => {
    try {
      const res = await api.get(`/recruitment/${id}/applications/${aid}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto" data-testid="recruitment-applicants">
        <button onClick={() => nav("/my-recruitment")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2C59]"><ArrowLeft className="h-4 w-4" /> My Recruitment</button>
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F2C59] flex items-center gap-2"><Users className="h-6 w-6" /> Applicants</h1>
            {opp && <p className="text-sm text-slate-500 mt-0.5">{opp.title}</p>}
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="applicants-filter" className="w-48 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {apps === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : apps.length === 0 ? (
          <div data-testid="applicants-empty" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-16 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No applicants{filter !== "all" ? " in this stage" : " yet"}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => (
              <div key={a.id} data-testid={`applicant-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#0F2C59]">{a.applicant_name || "Applicant"}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STAGE_STYLE[a.pipeline_status]}`}>{a.pipeline_status}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-slate-500">
                      {a.applicant_email && <a href={`mailto:${a.applicant_email}`} className="flex items-center gap-1 hover:text-[#0F2C59]"><Mail className="h-3.5 w-3.5" />{a.applicant_email}</a>}
                      {a.applicant_mobile && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{a.applicant_mobile}</span>}
                    </div>
                    {a.cover_note && <p className="mt-2 text-[13px] text-slate-600 whitespace-pre-line">{a.cover_note}</p>}
                    {a.has_cv && (
                      <button data-testid={`applicant-cv-${a.id}`} onClick={() => downloadCv(a.id)} className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0F2C59] hover:underline"><FileText className="h-4 w-4" /> View CV <Download className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                  <Select value={a.pipeline_status} onValueChange={(v) => changeStage(a.id, v)}>
                    <SelectTrigger data-testid={`applicant-stage-${a.id}`} className="w-40 rounded-xl shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
